import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Session } from '../types';

const SessionApprovalPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'confirmed' | 'rejected' | 'all'>('pending');

  useEffect(() => {
    if (eventId) {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/events/${eventId}/sessions`);
      setSessions(response.data.data || []);
    } catch (err: any) {
      console.error('Error fetching sessions:', err);
      setError(err.response?.data?.error?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to approve this session?')) {
      return;
    }

    try {
      await apiClient.post(`/v1/sessions/${sessionId}/approve`);
      await fetchSessions();
    } catch (err: any) {
      console.error('Error approving session:', err);
      alert(err.response?.data?.error?.message || 'Failed to approve session');
    }
  };

  const handleReject = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to reject this session? The speaker will be notified.')) {
      return;
    }

    try {
      await apiClient.post(`/v1/sessions/${sessionId}/reject`);
      await fetchSessions();
    } catch (err: any) {
      console.error('Error rejecting session:', err);
      alert(err.response?.data?.error?.message || 'Failed to reject session');
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { class: 'bg-warning', icon: 'clock' },
      confirmed: { class: 'bg-success', icon: 'check-circle' },
      rejected: { class: 'bg-danger', icon: 'times-circle' }
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    if (filter === 'pending') return session.status === 'draft';
    return session.status === filter;
  });

  const pendingCount = sessions.filter(s => s.status === 'draft').length;
  const confirmedCount = sessions.filter(s => s.status === 'confirmed').length;
  const rejectedCount = sessions.filter(s => s.status === 'rejected').length;

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="mb-3">
        <Link to={`/events/${eventId}`} className="btn btn-link text-decoration-none">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Event
        </Link>
      </div>

      <h2 className="mb-4">
        <i className="fas fa-tasks me-2"></i>
        Session Approval
      </h2>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="card mb-4">
        <div className="card-body">
          <ul className="nav nav-pills">
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'pending' ? 'active' : ''}`}
                onClick={() => setFilter('pending')}
              >
                <i className="fas fa-clock me-1"></i>
                Pending
                {pendingCount > 0 && (
                  <span className="badge bg-warning text-dark ms-2">{pendingCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'confirmed' ? 'active' : ''}`}
                onClick={() => setFilter('confirmed')}
              >
                <i className="fas fa-check me-1"></i>
                Confirmed
                {confirmedCount > 0 && (
                  <span className="badge bg-success ms-2">{confirmedCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'rejected' ? 'active' : ''}`}
                onClick={() => setFilter('rejected')}
              >
                <i className="fas fa-times me-1"></i>
                Rejected
                {rejectedCount > 0 && (
                  <span className="badge bg-danger ms-2">{rejectedCount}</span>
                )}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                <i className="fas fa-list me-1"></i>
                All
              </button>
            </li>
          </ul>
        </div>
      </div>

      {filteredSessions.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h4>No Sessions</h4>
            <p className="text-muted">
              {filter === 'pending'
                ? 'No sessions pending approval.'
                : `No ${filter} sessions found.`}
            </p>
          </div>
        </div>
      ) : (
        <div className="row">
          {filteredSessions.map(session => {
            const statusBadge = getStatusBadge(session.status);
            return (
              <div key={session.session_id} className="col-12 mb-3">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="flex-grow-1">
                        <h5 className="mb-1">{session.talk_title || 'Untitled Talk'}</h5>
                        <p className="text-muted mb-2">
                          <i className="fas fa-user me-1"></i>
                          {session.speaker_name || 'Unknown Speaker'}
                        </p>
                      </div>
                      <span className={`badge ${statusBadge.class}`}>
                        <i className={`fas fa-${statusBadge.icon} me-1`}></i>
                        {session.status === 'draft' ? 'Pending' : session.status}
                      </span>
                    </div>

                    <div className="row mb-3">
                      {session.start_time && (
                        <div className="col-md-6">
                          <strong className="text-muted">Preferred Time:</strong>
                          <div>{new Date(session.start_time).toLocaleString()}</div>
                        </div>
                      )}
                      {session.room_or_track && (
                        <div className="col-md-6">
                          <strong className="text-muted">Preferred Room/Track:</strong>
                          <div>{session.room_or_track}</div>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <strong className="text-muted">Submitted:</strong>
                      <span className="ms-2">{new Date(session.created_at).toLocaleString()}</span>
                    </div>

                    <div className="d-flex gap-2">
                      <Link
                        to={`/sessions/${session.session_id}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="fas fa-eye me-1"></i>
                        View Details
                      </Link>
                      <Link
                        to={`/talks/${session.talk_id}`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        <i className="fas fa-microphone me-1"></i>
                        View Talk
                      </Link>
                      {session.status === 'draft' && (
                        <>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleApprove(session.session_id)}
                          >
                            <i className="fas fa-check me-1"></i>
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleReject(session.session_id)}
                          >
                            <i className="fas fa-times me-1"></i>
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SessionApprovalPage;
